import {
  Constructor,
  EdgePathCropper,
  Fill,
  GraphComponent,
  ICanvasContext,
  IEdgePathCropper,
  IGraph,
  INode,
  IPort,
  IRenderContext,
  Point,
  PortStyleBase,
  Rect,
  Size,
  Stroke,
  SvgVisual,
  TaggedSvgVisual
} from '@yfiles/yfiles/yfiles';

type PortFrameKind = 'single' /* ○ */ | 'bottom' /* ╰╯ */ | 'middle' /* ││ */ | 'top' /* ╭╮ */;
type PortSide = 'left' | 'right';

const portOffset = 10;
const portSize = 5;
const portPadding = 2;
const verticalPortDistance = 9.5;

/**
 * Updates the port styles for all ports in the graph so their kind and position matches the layout again.
 * @param graph The graph to update
 */
export function updatePortVisuals(graph: IGraph) {
  for (const node of graph.nodes) {
    const leftPorts = graph
      .inEdgesAt(node)
      .map((e) => e.targetPort)
      .toArray();
    const rightPorts = graph
      .outEdgesAt(node)
      .map((e) => e.sourcePort)
      .toArray();
    leftPorts.sort((a, b) => a.location.y - b.location.y);
    rightPorts.sort((a, b) => a.location.y - b.location.y);

    // Collect all visible ports
    const visiblePorts = new Set([...leftPorts, ...rightPorts]);
    
    // Hide all ports that are not visible (from filtered-out edges)
    for (const port of graph.ports.filter(p => p.owner === node)) {
      if (!visiblePorts.has(port)) {
        graph.setStyle(port, new HiddenPortStyle());
      }
    }

    updateSidePortStyles(graph, leftPorts, 'left');
    updateSidePortStyles(graph, rightPorts, 'right');
  }
}

function updateSidePortStyles(graph: IGraph, ports: IPort[], side: PortSide) {
  if (ports.length === 0) {
    return;
  }

  // Ports may share a location when multiple edges are grouped, so multiple ports may be the "first", etc.
  const firstY = ports[0].location.y;
  const lastY = ports.at(-1)!.location.y;
  const totalHeight = Math.abs(lastY - firstY);
  
  if (Math.abs(firstY - lastY) < 1) {
    // only a single visible port - only set isFirst for the actual first port
    for (let i = 0; i < ports.length; i++) {
      const port = ports[i];
      graph.setStyle(port, new ExteriorPortStyle('single', side, i === 0));
    }
  } else {
    for (let i = 0; i < ports.length; i++) {
      const port = ports[i];
      const y = port.location.y;
      let kind: PortFrameKind;
      const isFirst = i === 0;
      if (Math.abs(y - firstY) < 1) {
        kind = 'top';
      } else if (Math.abs(y - lastY) < 1) {
        kind = 'bottom';
      } else {
        kind = 'middle';
      }
      // For the first port, pass the total height so shadow can cover all ports
      if (isFirst) {
        (port as any).groupHeight = totalHeight;
      }
      graph.setStyle(port, new ExteriorPortStyle(kind, side, isFirst));
    }
  }
}

class HiddenPortStyle extends PortStyleBase<SvgVisual> {
  protected createVisual(_context: IRenderContext, _port: IPort): SvgVisual | null {
    return null;
  }

  protected updateVisual(_context: IRenderContext, _oldVisual: SvgVisual, _port: IPort): SvgVisual | null {
    return null;
  }

  protected getBounds(_context: ICanvasContext, _port: IPort): Rect {
    return Rect.EMPTY;
  }
}

class ExteriorPortStyle extends PortStyleBase<TaggedSvgVisual<SVGGElement, PortFrameKind>> {
  constructor(
    public frame: PortFrameKind,
    public side: PortSide,
    public isFirst: boolean = false
  ) {
    super();
  }

  protected createVisual(
    context: IRenderContext,
    port: IPort
  ): TaggedSvgVisual<SVGGElement, PortFrameKind> | null {
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    
    const frame = createFrame(this.frame);
    frame.setAttribute('fill', 'rgba(255, 255, 255, 0)'); // Completely transparent background
    frame.setAttribute('stroke', '#959595');
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.r.baseVal.value = portSize / 2;
    getPortColor(context, port).applyTo(circle, context);
    
    // Add animations for active ports (this applies glow to circle only)
    // addPortAnimations(circle, port, context); // Removed - animation moved to shadow
    
    // Only create shadow for the first port in the group when port is red (health < 75) and is a target port
    if (this.isFirst) {
      // Check if this port receives animated dots (target port)
      let isTargetPort = false;
      let targetNodeType: string | null = null;
      if (context.canvasComponent instanceof GraphComponent) {
        const graph = context.canvasComponent.graph;
        for (const edge of graph.inEdgesAt(port)) {
          if (edge.style && edge.style.constructor.name === 'DotEdgeStyle') {
            isTargetPort = true;
            targetNodeType = edge.targetNode?.tag?.type || null; // Get target node type
            break;
          }
        }
      }
      
      // Check node health to determine if shadow should be red, but only for target ports
      if (isTargetPort) {
        const nodeHealth = getNodeHealth(port.owner as INode);
        if (nodeHealth < 75) {
          const groupHeight = (port as any).groupHeight || 0;
          
          // Determine shadow width based on target node type
          let shadowWidth = 60; // Default width
          if (targetNodeType) {
            switch (targetNodeType) {
              case 'CAPABILITY':
                shadowWidth = 35;  // Shortest for business capabilities
                break;
              case 'APPLICATION_GROUP':
              case 'TEAM':
                shadowWidth = 45;  // Medium for groups/teams
                break;
              case 'LEAF':
                shadowWidth = 55;  // Longest for leaf nodes
                break;
              default:
                shadowWidth = 55;  // Default for unknown types
            }
          }
          
          // Calculate x-offset based on shadow width - smaller rectangles closer to connector
          const xOffset =-5-(shadowWidth / 2); // Closer for smaller shadows
          const shadow = createShadow(xOffset, groupHeight / 2, shadowWidth, groupHeight + 10); // Red shadow
          g.append(shadow, frame, circle);
        } else {
          g.append(frame, circle);
        }
      } else {
        g.append(frame, circle);
      }
    } else {
      g.append(frame, circle);
    }
    const location = port.location;
    const owner = port.owner as INode;
    SvgVisual.setTranslate(
      g,
      this.side === 'left' ? owner.layout.x - portOffset : owner.layout.maxX + portOffset,
      location.y
    );

    return SvgVisual.from(g, this.frame);
  }

  protected updateVisual(
    context: IRenderContext,
    oldVisual: TaggedSvgVisual<SVGGElement, PortFrameKind>,
    port: IPort
  ) {
    if (oldVisual instanceof SvgVisual && (typeof oldVisual.tag as PortFrameKind) === this.frame) {
      const g = oldVisual.svgElement;
      if (g instanceof SVGGElement && oldVisual.tag) {
        const location = port.location;
        const owner = port.owner as INode;
        SvgVisual.setTranslate(
          g,
          this.side === 'left' ? owner.layout.x - portOffset : owner.layout.maxX + portOffset,
          location.y
        );
        return oldVisual;
      }
    }
    context.childVisualRemoved(oldVisual);
    return this.createVisual(context, port);
  }

  protected getBounds(context: ICanvasContext, port: IPort): Rect {
    const location = port.location;
    const owner = port.owner as INode;
    const radius = portSize / 2 + portPadding;
    const size = new Size(radius * 2 + 1, verticalPortDistance);
    const actualLocation = new Point(
      this.side === 'left' ? owner.layout.x - portOffset : owner.layout.maxX + portOffset,
      location.y
    );
    return Rect.fromCenter(actualLocation, size);
  }

  protected lookup(port: IPort, type: Constructor): any {
    if (type === IEdgePathCropper) {
      return new EdgePathCropper({
        extraCropLength: portOffset + portSize / 2 + portPadding
      });
    }
    return super.lookup(port, type);
  }
}

function addPortAnimations(circle: SVGCircleElement, port: IPort, context: IRenderContext): void {
  // Check if port has animated edges (Time metric flows) AND is a target port (where dots end)
  const hasAnimatedFlow = checkForAnimatedFlow(port, context);
  const isTargetPort = isTargetPortForAnimatedFlow(port, context);
  
  if (hasAnimatedFlow && isTargetPort) {
    // Calculate animation speed based on target node health (this node's health)
    const nodeHealth = getNodeHealth(port.owner as INode);
    const animationDuration = calculateAnimationDuration(nodeHealth);
    
    // Add pulse animation for active flows with health-based speed
    const pulseAnimate = document.createElementNS('http://www.w3.org/2000/svg', 'animate');
    pulseAnimate.setAttribute('attributeName', 'r');
    pulseAnimate.setAttribute('values', `${portSize / 2};${portSize / 2 + 1};${portSize / 2}`);
    pulseAnimate.setAttribute('dur', `${animationDuration}s`);
    pulseAnimate.setAttribute('repeatCount', 'indefinite');
    circle.appendChild(pulseAnimate);
  }
}

function isTargetPortForAnimatedFlow(port: IPort, context: IRenderContext): boolean {
  // Check if this port is a target for any animated edge (where dots end)
  if (context.canvasComponent instanceof GraphComponent) {
    const graph = context.canvasComponent.graph;
    for (const edge of graph.inEdgesAt(port)) {
      if (edge.style && edge.style.constructor.name === 'DotEdgeStyle') {
        return true; // This port receives animated dots
      }
    }
  }
  return false;
}

function getNodeHealth(node: INode): number {
  // Get the health percentage for the given node
  const health = node?.tag?.percentage?.TimePercentage;
  return typeof health === 'number' ? health : 100; // Default to healthy
}

function calculateAnimationDuration(health: number): number {
  // Health-based animation duration: healthier = faster animation
  // Health 100% -> 1.5s (fast pulse)
  // Health 50%  -> 3s (medium pulse)  
  // Health 0%   -> 6s (slow pulse)
  const minDuration = 1.5;
  const maxDuration = 6;
  const healthRatio = Math.max(0, Math.min(1, health / 100));
  
  // Use exponential curve for more dramatic difference
  const speedMultiplier = 0.25 + 0.75 * Math.pow(healthRatio, 2);
  return minDuration + (maxDuration - minDuration) * (1 - speedMultiplier);
}

function checkForAnimatedFlow(port: IPort, context: IRenderContext): boolean {
  // Check if any connected edge has animated style (DotEdgeStyle)
  if (context.canvasComponent instanceof GraphComponent) {
    const graph = context.canvasComponent.graph;
    for (const edge of graph.edgesAt(port)) {
      if (edge.style && edge.style.constructor.name === 'DotEdgeStyle') {
        return true;
      }
    }
  }
  return false;
}

function getPortColor(context: IRenderContext, port: IPort): Fill {
  // Check if this is a target port for animated flow and get health-based color
  if (context.canvasComponent instanceof GraphComponent) {
    const graph = context.canvasComponent.graph;
    
    // Check if this port receives animated dots (target port)
    for (const edge of graph.inEdgesAt(port)) {
      if (edge.style && edge.style.constructor.name === 'DotEdgeStyle') {
        // Get target node health for color determination
        const nodeHealth = getNodeHealth(port.owner as INode);
        return getHealthBasedFillColor(nodeHealth);
      }
    }
    
    // For non-animated ports, use edge color or default
    const edge = graph.edgesAt(port).at(0);
    if (
      edge &&
      'stroke' in edge.style &&
      edge.style.stroke instanceof Stroke &&
      edge.style.stroke.fill
    ) {
      return edge.style.stroke.fill;
    }
  }
  return Fill.from('#959595');
}

function getHealthBasedFillColor(health: number): Fill {
  // Return health-based colors matching the color scheme
  if (health >= 90) {
    return Fill.from('#0F7F02'); // GOOD - Green
  } else if (health >= 75) {
    return Fill.from('#ffd268'); // WARNING - Yellow  
  } else {
    return Fill.from('#CF2F34'); // BAD - Red
  }
}

/**
 * Creates a rectangular shadow element for ports
 * @param x - Horizontal position of shadow center (relative to port)
 * @param y - Vertical position of shadow center (relative to port)
 * @param width - Width of the rectangle (default: 80)
 * @param height - Height of the rectangle (matches port group height)
 * @param color - Shadow color with opacity (default: semi-transparent black)
 * @returns SVG rect element representing the shadow
 */
function createShadow(x: number, y: number, width: number = 80, height: number = 40, color: string = 'rgba(207, 47, 52, 0.4)'): SVGRectElement {
  const shadow = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  shadow.setAttribute('x', (x - width / 2).toString());
  shadow.setAttribute('y', (y - height / 2).toString());
  shadow.setAttribute('width', width.toString());
  shadow.setAttribute('height', height.toString());
  shadow.setAttribute('fill', color);
  shadow.setAttribute('filter', 'blur(2px)');
  
  // Add opacity pulsating animation
  const opacityAnimate = document.createElementNS('http://www.w3.org/2000/svg', 'animate');
  opacityAnimate.setAttribute('attributeName', 'opacity');
  opacityAnimate.setAttribute('values', '0.4;1;0.4');
  opacityAnimate.setAttribute('dur', '2s');
  opacityAnimate.setAttribute('repeatCount', 'indefinite');
  shadow.appendChild(opacityAnimate);
  
  return shadow;
}

function createFrame(frame: PortFrameKind): SVGElement {
  const radius = portSize / 2 + portPadding;
  switch (frame) {
    case 'single':
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.r.baseVal.value = radius;
      circle.setAttribute('filter', 'url(#portShadow)');
      return circle;
    case 'top':
      const topPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      topPath.setAttribute(
        'd',
        `M ${-radius} ${verticalPortDistance / 2} V 0 A ${radius} ${radius} 0 0 1 ${radius} 0 V ${verticalPortDistance / 2}`
      );
      topPath.setAttribute('filter', 'url(#portShadow)');
      return topPath;
    case 'middle':
      const fill = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      fill.x.baseVal.value = -radius;
      fill.y.baseVal.value = -verticalPortDistance / 2;
      fill.width.baseVal.value = 2 * radius;
      fill.height.baseVal.value = verticalPortDistance;
      fill.setAttribute('stroke', 'none');
      fill.setAttribute('filter', 'url(#portShadow)');
      const border = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      border.setAttribute(
        'd',
        `M ${-radius} ${verticalPortDistance / 2} V ${-verticalPortDistance / 2} m ${2 * radius} 0 V ${verticalPortDistance / 2}`
      );
      border.setAttribute('filter', 'url(#portShadow)');
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.append(fill, border);
      return g;
    case 'bottom':
      const bottomPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      bottomPath.setAttribute(
        'd',
        `M ${radius} ${-verticalPortDistance / 2} V 0 A ${radius} ${radius} 0 0 1 ${-radius} 0 V ${-verticalPortDistance / 2}`
      );
      bottomPath.setAttribute('filter', 'url(#portShadow)');
      return bottomPath;
  }
}
